import { motion } from 'framer-motion'
import {
  Bitcoin, Shield, TrendingUp, AlertTriangle, Lock, Globe,
  ArrowRight, Wallet, BarChart3, Zap
} from 'lucide-react'
import { Link } from 'react-router-dom'
import AnimatedSection from '../components/AnimatedSection'
import SectionHeading from '../components/SectionHeading'

const cryptoTopics = [
  {
    icon: Bitcoin,
    title: 'Bitcoin & Blockchain Basics',
    description: 'Understand how blockchain technology works, what makes Bitcoin unique, and why it is called digital gold.',
  },
  {
    icon: Wallet,
    title: 'Crypto Wallets & Security',
    description: 'Learn the difference between hot and cold wallets, private keys, seed phrases, and how to keep your assets safe.',
  },
  {
    icon: BarChart3,
    title: 'Crypto Technical Analysis',
    description: 'Apply traditional TA to crypto markets. Understand volatility, liquidity, and crypto-specific chart patterns.',
  },
  {
    icon: TrendingUp,
    title: 'Altcoin Research',
    description: 'Evaluate altcoins beyond the hype. Learn tokenomics, use cases, team analysis, and red flags to watch for.',
  },
  {
    icon: Lock,
    title: 'DeFi & Yield Farming',
    description: 'Explore decentralized finance, staking, liquidity pools, and yield farming opportunities with risk awareness.',
  },
  {
    icon: Globe,
    title: 'Global Crypto Regulations',
    description: 'Stay informed about evolving crypto regulations worldwide and how they impact your trading and investment strategy.',
  },
]

const risks = [
  { icon: AlertTriangle, title: 'High Volatility', desc: 'Crypto prices can swing 10-50% in a single day. Never invest more than you can afford to lose.' },
  { icon: Shield, title: 'Scams & Hacks', desc: 'The crypto space has scams, rug pulls, and exchange hacks. Always DYOR (Do Your Own Research).' },
  { icon: Lock, title: 'No Recovery', desc: 'Lost private keys or sent to wrong addresses mean permanent loss. There is no customer support in DeFi.' },
]

export default function Crypto() {
  return (
    <div className="min-h-screen pt-24 md:pt-28">
      {/* Hero */}
      <section className="relative pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-brand-500/5 via-transparent to-brand-600/5" />
        <div className="absolute top-0 right-1/4 w-96 h-96 bg-brand-500/10 rounded-full blur-[120px]" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-block px-4 py-1.5 rounded-full text-xs font-semibold tracking-wide uppercase bg-brand-500/10 text-brand-600 dark:text-brand-400 mb-4"
          >
            Cryptocurrency
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-[var(--text-primary)] mb-6 text-balance"
          >
            Navigate the <span className="gradient-text">Crypto</span> Markets
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg text-[var(--text-secondary)] max-w-2xl mx-auto text-balance"
          >
            Cryptocurrency trading requires a different mindset. Learn the fundamentals of blockchain,
            crypto technical analysis, and risk management specific to digital assets.
          </motion.p>
        </div>
      </section>

      {/* Topics */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            subtitle="Crypto Education"
            title="Key Areas of Focus"
            description="From Bitcoin basics to DeFi strategies, we cover the essential knowledge for crypto traders."
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {cryptoTopics.map((topic, i) => (
              <AnimatedSection key={i} delay={i * 0.1}>
                <div className="group glass rounded-3xl p-8 h-full card-hover glow-border">
                  <div className="w-12 h-12 rounded-2xl bg-brand-500/10 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300">
                    <topic.icon size={24} className="text-brand-600 dark:text-brand-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-2">{topic.title}</h3>
                  <p className="text-sm text-[var(--text-secondary)] leading-relaxed">{topic.description}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Risk Warning */}
      <section className="py-20 bg-[var(--bg-secondary)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            subtitle="Risk Awareness"
            title="Understand the Risks"
            description="Crypto markets are highly volatile and speculative. Educate yourself on the risks before investing."
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {risks.map((risk, i) => (
              <AnimatedSection key={i} delay={i * 0.1}>
                <div className="glass rounded-3xl p-8 text-center card-hover border border-red-500/10">
                  <div className="w-12 h-12 rounded-2xl bg-red-500/10 flex items-center justify-center mx-auto mb-5">
                    <risk.icon size={24} className="text-red-500" />
                  </div>
                  <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-2">{risk.title}</h3>
                  <p className="text-sm text-[var(--text-secondary)] leading-relaxed">{risk.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-brand-600/10 via-transparent to-brand-800/10" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <AnimatedSection>
            <div className="w-16 h-16 rounded-2xl bg-brand-500/10 flex items-center justify-center mx-auto mb-6">
              <Zap size={28} className="text-brand-600 dark:text-brand-400" />
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-[var(--text-primary)] mb-6 text-balance">
              Start Your Crypto <span className="gradient-text">Journey</span>
            </h2>
            <p className="text-lg text-[var(--text-secondary)] mb-10 max-w-2xl mx-auto text-balance">
              Dive deeper into crypto trading strategies and market analysis on our YouTube channel.
              New videos every week covering the latest trends and techniques.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="https://www.youtube.com/@fivetrader"
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center gap-2 px-8 py-4 bg-brand-600 hover:bg-brand-700 text-white font-semibold rounded-2xl transition-all duration-300 shadow-lg shadow-brand-500/25 hover:shadow-brand-500/40 hover:-translate-y-0.5"
              >
                Watch Crypto Videos
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </a>
              <Link
                to="/learn"
                className="inline-flex items-center gap-2 px-8 py-4 glass text-[var(--text-primary)] font-semibold rounded-2xl hover:bg-[var(--bg-secondary)] transition-all duration-300"
              >
                Back to Learning
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  )
}
